import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, X, Check, AlertTriangle, Info, CheckCircle, XCircle,
  Phone, PhoneIncoming, User, DollarSign, Settings, Shield,
  Clock, Star, Zap, MessageSquare, Calendar, TrendingUp,
  Download, Eye, MoreHorizontal, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';
import { webSocketService, RealTimeEvent } from '@/services/websocket';
import { useAuthStore } from '@/store/auth';

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'call' | 'system' | 'investment' | 'transaction';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  action_url?: string;
  data?: any;
  priority: 'low' | 'medium' | 'high' | 'critical';
  from_user?: {
    id: string;
    name: string;
    avatar?: string;
  };
  expires_at?: string;
}

interface NotificationStats {
  total: number;
  unread: number;
  today: number;
  this_week: number;
}

const RealTimeNotifications = () => {
  const { user } = useAuthStore();
  const t = (tr: string, de: string) => (user?.role === 'staff' ? de : tr);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState<NotificationStats>({
    total: 0,
    unread: 0,
    today: 0,
    this_week: 0
  });
  const [filter, setFilter] = useState<'all' | 'unread' | 'important'>('all');
  const [isConnected, setIsConnected] = useState(false);

  // WebSocket connection setup - SIMPLIFIED
  useEffect(() => {
    if (!user) {
      setIsConnected(false);
      return;
    }

    // Set current user for WebSocket service
    webSocketService.setCurrentUser(user);

    // Simple event handlers
    const handleConnected = () => {
      setIsConnected(true);
    };

    const handleDisconnected = () => {
      setIsConnected(false);
    };

    webSocketService.on('connected', handleConnected);
    webSocketService.on('disconnected', handleDisconnected);

    return () => {
      webSocketService.off('connected', handleConnected);
      webSocketService.off('disconnected', handleDisconnected);
    };
  }, [user]);

  // Subscribe to real-time events
  useEffect(() => {
    if (!isConnected) return;

    const subscriptions: string[] = [];

    // User notifications
    subscriptions.push(
      webSocketService.subscribe('user.notification', handleNotification)
    );

    // Call center events
    if (user?.role === 'staff' || user?.role === 'admin') {
      subscriptions.push(
        webSocketService.subscribe('call.incoming', (data) => {
          addNotification({
            type: 'call',
            title: 'Gelen Arama',
            message: `${data.customer.name} aranıyor`,
            priority: 'high',
            data
          });
        })
      );

      subscriptions.push(
        webSocketService.subscribe('call.waiting', (data) => {
          addNotification({
            type: 'call',
            title: 'Bekleyen Arama',
            message: `Kuyrukta ${data.queue_position}. sırada, ${data.wait_time}s bekliyor`,
            priority: 'medium',
            data
          });
        })
      );
    }

    // Investment events for customers
    if (user?.role === 'customer') {
      subscriptions.push(
        webSocketService.subscribe('investment.matured', (data) => {
          addNotification({
            type: 'investment',
            title: 'Yatırım Vade Sonu',
            message: `${data.final_amount} € tutarında yatırımınız vadesi doldu`,
            priority: 'high',
            data
          });
        })
      );

      subscriptions.push(
        webSocketService.subscribe('transaction.completed', (data) => {
          addNotification({
            type: 'transaction',
            title: 'İşlem Tamamlandı',
            message: `${data.amount} € ${data.type} işleminiz başarıyla tamamlandı`,
            priority: 'medium',
            data
          });
        })
      );
    }

    // System events for all users
    subscriptions.push(
      webSocketService.subscribe('system.broadcast', (data) => {
        const shouldShow = !data.target_roles || (user?.role ? data.target_roles.includes(user.role) : false);
        if (shouldShow) {
          addNotification({
            type: 'system',
            title: 'Sistem Bildirimi',
            message: data.message,
            priority: (['low','medium','high','critical'].includes(data.priority) ? data.priority : 'medium') as 'low' | 'medium' | 'high' | 'critical'
          });
        }
      })
    );

    subscriptions.push(
      webSocketService.subscribe('system.maintenance', (data) => {
        addNotification({
          type: 'warning',
          title: 'Bakım Bildirimi',
          message: `${data.message} - Tahmini süre: ${data.estimated_duration} dakika`,
          priority: 'high',
          data
        });
      })
    );

    // Dashboard alerts for admin/staff
    if (user?.role === 'admin' || user?.role === 'staff') {
      subscriptions.push(
        webSocketService.subscribe('dashboard.alert', (data) => {
          addNotification({
            type: data.severity === 'critical' ? 'error' : 'warning',
            title: 'Sistem Uyarısı',
            message: data.message,
            priority: data.severity as any
          });
        })
      );
    }

    // Cleanup subscriptions
    return () => {
      subscriptions.forEach(id => {
        webSocketService.unsubscribe(id);
      });
    };
  }, [isConnected, user]);

  // Handle incoming notifications
  const handleNotification = useCallback((data: any) => {
    addNotification({
      type: data.type || 'info',
      title: data.title,
      message: data.message,
      priority: data.priority || 'medium',
      action_url: data.action_url,
      data: data.data
    });
  }, []);

  // Add new notification
  const addNotification = useCallback((notificationData: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const notification: Notification = {
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      read: false,
      ...notificationData
    };

    setNotifications(prev => [notification, ...prev]);
    updateStats();

    // Show toast for high priority notifications
    if (notification.priority === 'high' || notification.priority === 'critical') {
      if (notification.type === 'error' || notification.priority === 'critical') {
        toast.error(notification.message);
      } else if (notification.type === 'warning') {
        toast((t) => (
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-gold-600 mr-2" />
            <div>
              <div className="font-medium text-slate-900">{notification.title}</div>
              <div className="text-sm text-slate-600">{notification.message}</div>
            </div>
          </div>
        ));
      } else {
        toast.success(notification.message);
      }
    }

    // Auto-expire notifications if specified
    if (notification.expires_at) {
      const expiryTime = new Date(notification.expires_at).getTime() - Date.now();
      if (expiryTime > 0) {
        setTimeout(() => {
          removeNotification(notification.id);
        }, expiryTime);
      }
    }
  }, []);

  // Remove notification
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    updateStats();
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n =>
      n.id === id ? { ...n, read: true } : n
    ));
    updateStats();
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    updateStats();
  }, []);

  // Update notification stats
  const updateStats = useCallback(() => {
    setStats(prev => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      return {
        total: notifications.length,
        unread: notifications.filter(n => !n.read).length,
        today: notifications.filter(n => new Date(n.timestamp) >= today).length,
        this_week: notifications.filter(n => new Date(n.timestamp) >= weekAgo).length
      };
    });
  }, [notifications]);

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    switch (filter) {
      case 'unread':
        return !notification.read;
      case 'important':
        return notification.priority === 'high' || notification.priority === 'critical';
      default:
        return true;
    }
  });

  // Get notification icon
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'call':
        return PhoneIncoming;
      case 'success':
        return CheckCircle;
      case 'error':
        return XCircle;
      case 'warning':
        return AlertTriangle;
      case 'investment':
        return TrendingUp;
      case 'transaction':
        return DollarSign;
      case 'system':
        return Settings;
      default:
        return Info;
    }
  };

  // Get notification color
  const getNotificationColor = (type: Notification['type'], priority: Notification['priority']) => {
    if (priority === 'critical') return 'text-danger-600 bg-danger-50';

    switch (type) {
      case 'call':
        return 'text-primary-600 bg-primary-50';
      case 'success':
        return 'text-success-600 bg-success-50';
      case 'error':
        return 'text-danger-600 bg-danger-50';
      case 'warning':
        return 'text-gold-700 bg-gold-50';
      case 'investment':
        return 'text-gold-700 bg-gold-50';
      case 'transaction':
        return 'text-success-700 bg-success-100';
      case 'system':
        return 'text-slate-600 bg-slate-100';
      default:
        return 'text-primary-600 bg-primary-50';
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);

    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Şimdi';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return time.toLocaleDateString('tr-TR');
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        size="sm"
        className="relative p-2"
      >
        <Bell className={`w-5 h-5 ${isConnected ? 'text-slate-700' : 'text-slate-400'}`} />

        {stats.unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-primary-700 text-white text-[10px] font-semibold tabular-nums w-[18px] h-[18px] flex items-center justify-center border-2 border-white">
            {stats.unread > 99 ? '99+' : stats.unread}
          </span>
        )}

        {/* Connection indicator */}
        <div className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-white ${
          isConnected ? 'bg-success-500' : 'bg-danger-500'
        }`} />
      </Button>

      {/* Notification Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-none shadow-[0_20px_60px_rgba(16,24,40,0.18)] border border-slate-200 z-50 max-h-[28rem] overflow-hidden">
            {/* Accent hairline */}
            <div className="h-[3px] bg-gradient-to-r from-primary-600 to-gold-500" />

            {/* Header */}
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-lg font-medium text-slate-900">{t('Bildirimler','Benachrichtigungen')}</h3>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-success-500' : 'bg-danger-500'}`} />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{isConnected ? t('Bağlı','Verbunden') : t('Bağlantı kesildi','Verbindung getrennt')}</span>
                </div>
              </div>

              {/* Stats */}
              <div className="flex space-x-4 text-sm text-slate-600 mb-3">
                <span><span className="tabular-nums font-medium text-slate-900">{stats.total}</span> {t('toplam','gesamt')}</span>
                <span><span className="tabular-nums font-medium text-slate-900">{stats.unread}</span> {t('okunmamış','ungelesen')}</span>
                <span><span className="tabular-nums font-medium text-slate-900">{stats.today}</span> {t('bugün','heute')}</span>
              </div>

              {/* Filter tabs */}
              <div className="flex border border-slate-200 divide-x divide-slate-200">
                {[
                  { key: 'all', label: t('Tümü','Alle') },
                  { key: 'unread', label: t('Okunmamış','Ungelesen') },
                  { key: 'important', label: t('Önemli','Wichtig') }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key as any)}
                    className={`flex-1 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                      filter === tab.key
                        ? 'bg-primary-700 text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Actions */}
              {stats.unread > 0 && (
                <div className="mt-3 flex justify-end">
                  <Button
                    onClick={markAllAsRead}
                    size="sm"
                    variant="outline"
                    className="text-xs rounded-none"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    {t('Tümünü Okundu İşaretle','Alle als gelesen markieren')}
                  </Button>
                </div>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-64 overflow-y-auto">
              {filteredNotifications.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>{t('Henüz bildirim yok','Noch keine Benachrichtigungen')}</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredNotifications.map((notification) => {
                    const IconComponent = getNotificationIcon(notification.type);
                    const colorClass = getNotificationColor(notification.type, notification.priority);

                    return (
                      <div
                        key={notification.id}
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors border-l-4 ${
                          !notification.read ? 'border-gold-500 bg-primary-50/50' : 'border-transparent'
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-none shrink-0 ${colorClass}`}>
                            <IconComponent className="w-4 h-4" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`text-sm font-medium text-slate-900 ${
                                !notification.read ? 'font-semibold' : ''
                              }`}>
                                {notification.title}
                              </p>
                              <div className="flex items-center space-x-1">
                                {notification.priority === 'critical' && (
                                  <div className="w-1.5 h-1.5 bg-danger-600" />
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeNotification(notification.id);
                                  }}
                                  className="text-slate-400 hover:text-slate-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            <p className="text-sm text-slate-600 mt-1">
                              {notification.message}
                            </p>

                            <p className="text-xs text-slate-400 mt-2 tabular-nums">
                              {(() => {
                                if (user?.role !== 'staff') return formatTimeAgo(notification.timestamp);
                                // Simple German timeago for staff
                                const now = new Date();
                                const time = new Date(notification.timestamp);
                                const diff = Math.floor((now.getTime() - time.getTime())/60000);
                                if (diff < 1) return 'Jetzt';
                                if (diff < 60) return `${diff} Min. zuvor`;
                                const h = Math.floor(diff/60);
                                if (h < 24) return `${h} Std. zuvor`;
                                const d = Math.floor(h/24);
                                if (d < 7) return `${d} Tage zuvor`;
                                return time.toLocaleDateString('de-DE');
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RealTimeNotifications;
